import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/hooks/use-role";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StripeDebugPanel } from "@/components/stripe-debug-panel";
import { CronStatusPanel } from "@/components/cron-status-panel";
import { AdminAuctionsTab } from "@/components/admin/auctions-tab";
import { AdminOrdersTab } from "@/components/admin/orders-tab";
import { AdminUsersTab } from "@/components/admin/users-tab";
import { AdminDisputesTab } from "@/components/admin/disputes-tab";
import { AdminAuditLogTab } from "@/components/admin/audit-log-tab";
import { AdminPlatformTab } from "@/components/admin/platform-tab";
import { AdminAnalyticsTab } from "@/components/admin/analytics-tab";
import { AdminSellersReviewTab } from "@/components/admin/sellers-review-tab";
import { AdminSellersDirectoryTab } from "@/components/admin/sellers-directory-tab";
import { AdminReviewsTab } from "@/components/admin/reviews-tab";
import { AdminOverviewTab } from "@/components/admin/overview-tab";
import { AdminFinancialTab } from "@/components/admin/financial-tab";
import { AdminSystemHealthTab } from "@/components/admin/system-health-tab";
import { AdminWebhooksTab } from "@/components/admin/webhooks-tab";
import { AdminPaymentsTab } from "@/components/admin/payments-tab";
import { AdminPayoutsTab } from "@/components/admin/payouts-tab";
import { AdminShipmentsTab } from "@/components/admin/shipments-tab";
import { AdminReportsTab } from "@/components/admin/reports-tab";
import { AdminSettingsTab } from "@/components/admin/settings-tab";
import { AdminEmailCentreTab } from "@/components/admin/email-centre-tab";
import { AdminProductionChecklistTab } from "@/components/admin/production-checklist-tab";
import { AdminFeaturedPromotionsTab } from "@/components/admin/featured-promotions-tab";

import { AdminAiAnalyticsTab } from "@/components/admin/ai-analytics-tab";
import { AdminMarketSourcesTab } from "@/components/admin/market-sources-tab";
import { AdminCronHealthTab } from "@/components/admin/cron-health-tab";
import { AdminPushHealthTab } from "@/components/admin/push-health-tab";
import { AdminCardMasterTab } from "@/components/admin/card-master-tab";
import { AdminAdvertisingTab } from "@/components/admin/advertising-tab";
import { AdminHomepageStatsTab } from "@/components/admin/homepage-stats-tab";
import { AdminNewsletterTab } from "@/components/admin/newsletter-tab";
import { AdminCommunityUpdatesTab } from "@/components/admin/community-updates-tab";
import { AdminEventsTab } from "@/components/admin/events-tab";
import { AdminArticlesTab } from "@/components/admin/articles-tab";
import { AdminDealersTab } from "@/components/admin/dealers-tab";
import { AdminSellerTrustTab } from "@/components/admin/seller-trust-tab";
import { AdminRegistryTab } from "@/components/admin/registry-tab";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useTranslation } from "@/i18n";


export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Admin — BidNova Auctions" }] }),
  beforeLoad: async () => {
    const { data: userRes } = await supabase.auth.getUser();
    const user = userRes.user;
    if (!user) {
      throw redirect({ to: "/auth", search: { mode: "signin", redirect: "/admin" } });
    }
    const { data: rolesRows } = await supabase
      .from("user_roles").select("role").eq("user_id", user.id);
    const roles = (rolesRows ?? []).map((r) => r.role as string);
    if (!roles.includes("admin") && !roles.includes("moderator")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: AdminPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function AdminPage() {
  const { isAdmin, isAdminOrModerator, isLoading } = useRole();
  const { t } = useTranslation("admin");
  const [tab, setTab] = useState("overview");

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 text-muted-foreground">
        {t("page.loading", "Loading…")}
      </div>
    );
  }
  if (!isAdminOrModerator) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">{t("page.eyebrow", "Moderation")}</p>
        <h1 className="mt-2 font-display text-4xl">{t("page.title", "Admin dashboard")}</h1>
        <p className="mt-1 text-muted-foreground">
          {t("page.subtitle", "Review listings, orders, disputes, users and platform health.")}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto">
          <TabsTrigger value="overview">{t("tabs.overview", "Dashboard")}</TabsTrigger>
          <TabsTrigger value="users">{t("tabs.users", "Users")}</TabsTrigger>
          <TabsTrigger value="sellers">{t("tabs.sellers", "Sellers")}</TabsTrigger>
          <TabsTrigger value="sellers-review">{t("tabs.sellersReview", "Seller review")}</TabsTrigger>
          <TabsTrigger value="auctions">{t("tabs.auctions", "Auctions")}</TabsTrigger>
          <TabsTrigger value="orders">{t("tabs.orders", "Orders")}</TabsTrigger>
          <TabsTrigger value="payments">{t("tabs.payments", "Payments")}</TabsTrigger>
          <TabsTrigger value="payouts">{t("tabs.payouts", "Payouts")}</TabsTrigger>
          <TabsTrigger value="shipments">{t("tabs.shipments", "Shipments")}</TabsTrigger>
          <TabsTrigger value="disputes">{t("tabs.disputes", "Disputes")}</TabsTrigger>
          <TabsTrigger value="reports">{t("tabs.reports", "Reports")}</TabsTrigger>
          <TabsTrigger value="settings">{t("tabs.settings", "Settings")}</TabsTrigger>
          <TabsTrigger value="reviews">{t("tabs.reviews", "Reviews")}</TabsTrigger>
          <TabsTrigger value="financial">{t("tabs.financial", "Financial")}</TabsTrigger>
          <TabsTrigger value="health">{t("tabs.health", "System health")}</TabsTrigger>
          <TabsTrigger value="webhooks">{t("tabs.webhooks", "Webhooks")}</TabsTrigger>
          <TabsTrigger value="audit">{t("tabs.audit", "Audit log")}</TabsTrigger>
          <TabsTrigger value="promotions">{t("tabs.promotions", "Featured promos")}</TabsTrigger>

          <TabsTrigger value="ai-analytics">{t("tabs.aiAnalytics", "AI analytics")}</TabsTrigger>
          <TabsTrigger value="market-data">{t("tabs.marketData", "Market data")}</TabsTrigger>
          <TabsTrigger value="cron">{t("tabs.cron", "Cron health")}</TabsTrigger>
          <TabsTrigger value="push">{t("tabs.push", "Push health")}</TabsTrigger>
          <TabsTrigger value="card-master">{t("tabs.cardMaster", "Card master")}</TabsTrigger>
          <TabsTrigger value="email-centre">{t("tabs.emailCentre", "Email Centre")}</TabsTrigger>
          <TabsTrigger value="advertising">{t("tabs.advertising", "Advertising & Partners")}</TabsTrigger>
          <TabsTrigger value="homepage-stats">{t("tabs.homepageStats", "Homepage Statistics")}</TabsTrigger>
          <TabsTrigger value="newsletter">{t("tabs.newsletter", "Newsletter")}</TabsTrigger>
          <TabsTrigger value="community">{t("tabs.community", "Community")}</TabsTrigger>
          <TabsTrigger value="events">{t("tabs.events", "Events")}</TabsTrigger>
          <TabsTrigger value="articles">{t("tabs.articles", "Articles")}</TabsTrigger>
          <TabsTrigger value="dealers">{t("tabs.dealers", "Dealers")}</TabsTrigger>
          <TabsTrigger value="seller-trust">{t("tabs.sellerTrust", "Seller Trust")}</TabsTrigger>
          <TabsTrigger value="registry">{t("tabs.registry", "Registry")}</TabsTrigger>
          <TabsTrigger value="launch">{t("tabs.launch", "Launch checklist")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="stripe">{t("tabs.stripe", "Stripe debug")}</TabsTrigger>}

        </TabsList>

        <TabsContent value="overview"><AdminOverviewTab /></TabsContent>
        <TabsContent value="users"><AdminUsersTab isAdmin={!!isAdmin} /></TabsContent>
        <TabsContent value="sellers"><AdminSellersDirectoryTab /></TabsContent>
        <TabsContent value="sellers-review"><AdminSellersReviewTab /></TabsContent>
        <TabsContent value="auctions"><AdminAuctionsTab /></TabsContent>
        <TabsContent value="orders"><AdminOrdersTab /></TabsContent>
        <TabsContent value="payments"><AdminPaymentsTab /></TabsContent>
        <TabsContent value="payouts"><AdminPayoutsTab /></TabsContent>
        <TabsContent value="shipments"><AdminShipmentsTab /></TabsContent>
        <TabsContent value="disputes"><AdminDisputesTab onOpenOrder={() => setTab("orders")} /></TabsContent>
        <TabsContent value="reports"><AdminReportsTab /></TabsContent>
        <TabsContent value="settings"><AdminSettingsTab /></TabsContent>
        <TabsContent value="reviews"><AdminReviewsTab /></TabsContent>
        <TabsContent value="financial"><AdminFinancialTab /></TabsContent>
        <TabsContent value="analytics"><AdminAnalyticsTab /></TabsContent>
        <TabsContent value="health"><AdminSystemHealthTab /></TabsContent>
        <TabsContent value="webhooks"><AdminWebhooksTab /></TabsContent>
        <TabsContent value="audit"><AdminAuditLogTab /></TabsContent>
        <TabsContent value="promotions"><AdminFeaturedPromotionsTab /></TabsContent>
        
        <TabsContent value="ai-analytics"><AdminAiAnalyticsTab /></TabsContent>
        <TabsContent value="market-data"><AdminMarketSourcesTab /></TabsContent>
        <TabsContent value="cron"><AdminCronHealthTab /></TabsContent>
        <TabsContent value="push"><AdminPushHealthTab /></TabsContent>
        <TabsContent value="card-master"><AdminCardMasterTab /></TabsContent>
        <TabsContent value="email-centre"><AdminEmailCentreTab /></TabsContent>
        <TabsContent value="advertising"><AdminAdvertisingTab /></TabsContent>
        <TabsContent value="homepage-stats"><AdminHomepageStatsTab /></TabsContent>
        <TabsContent value="newsletter"><AdminNewsletterTab /></TabsContent>
        <TabsContent value="community"><AdminCommunityUpdatesTab /></TabsContent>
        <TabsContent value="events"><AdminEventsTab /></TabsContent>
        <TabsContent value="articles"><AdminArticlesTab /></TabsContent>
        <TabsContent value="dealers"><AdminDealersTab /></TabsContent>
        <TabsContent value="seller-trust"><AdminSellerTrustTab /></TabsContent>
        <TabsContent value="registry"><AdminRegistryTab /></TabsContent>
        <TabsContent value="launch"><AdminProductionChecklistTab /></TabsContent>
        {isAdmin && (
          <TabsContent value="stripe">
            <div className="space-y-6">
              <CronStatusPanel />
              <StripeDebugPanel />
            </div>
          </TabsContent>
        )}
      </Tabs>

    </div>
  );
}
